import Icon from '@/components/ui/AppIcon';

interface Office {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  hours: string;
  lat: number;
  lng: number;
}

const offices = [
  {
    id: '1',
    name: 'Centronics IT Solutions',
    address: 'Office 14 baldiyah street',
    city: 'Aziziya, Jeddah',
    state: 'Saudi Arabia',
    zip: '',
    phone: '+966 55 2258277',
    email: 'info@centronics.com.sa',
    hours: 'Sun-Thu: 8AM-6PM',
    lat: 21.5433,
    lng: 39.1728
  }
];

export default function LocationInfo() {
  return (
    <section className="py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Our Office Locations
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Visit us at any of our locations or reach out to the office nearest you for personalized support.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {offices.map((office) => (
            <div
              key={office.id}
              className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              {/* Map */}
              <div className="w-full h-48 bg-muted relative overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${office.name} Location`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${office.lat},${office.lng}&z=14&output=embed`}
                  className="absolute inset-0"
                />
              </div>

              {/* Office Info */}
              <div className="p-6">
                <h3 className="font-headline font-bold text-xl text-primary mb-4">
                  {office.name}
                </h3>

                <div className="space-y-3">
                  {/* Address */}
                  <div className="flex items-start space-x-3">
                    <Icon name="MapPinIcon" size={20} className="text-brand-secondary mt-0.5 flex-shrink-0" />
                    <div className="font-body text-sm text-muted-foreground">
                      <p>{office.address}</p>
                      <p>{office.city}, {office.state} {office.zip}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center space-x-3">
                    <Icon name="PhoneIcon" size={20} className="text-brand-secondary flex-shrink-0" />
                    <a
                      href={`tel:${office.phone}`}
                      className="font-body text-sm text-brand-primary hover:underline"
                    >
                      {office.phone}
                    </a>
                  </div>

                  {/* Email */}
                  <div className="flex items-center space-x-3">
                    <Icon name="EnvelopeIcon" size={20} className="text-brand-secondary flex-shrink-0" />
                    <a
                      href={`mailto:${office.email}`}
                      className="font-body text-sm text-brand-primary hover:underline"
                    >
                      {office.email}
                    </a>
                  </div>

                  {/* Hours */}
                  <div className="flex items-center space-x-3">
                    <Icon name="ClockIcon" size={20} className="text-brand-secondary flex-shrink-0" />
                    <span className="font-body text-sm text-muted-foreground">
                      {office.hours}
                    </span>
                  </div>
                </div>

                {/* Get Directions Button */}
                <button className="w-full mt-6 flex items-center justify-center space-x-2 px-4 py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-300">
                  <Icon name="MapIcon" size={18} variant="outline" />
                  <span>Get Directions</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 bg-muted rounded-lg p-6 lg:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="GlobeAltIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-headline font-bold text-lg text-primary mb-2">
                Remote Support Available
              </h4>
              <p className="font-body text-sm text-muted-foreground">
                We provide comprehensive remote IT support to clients nationwide, regardless of physical location.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="TruckIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-headline font-bold text-lg text-primary mb-2">
                On-Site Services
              </h4>
              <p className="font-body text-sm text-muted-foreground">
                Our technicians can visit your location for installations, maintenance, and emergency support.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-conversion-accent rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="ShieldCheckIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-headline font-bold text-lg text-primary mb-2">
                Secure Communication
              </h4>
              <p className="font-body text-sm text-muted-foreground">
                All client communications are encrypted and handled with the highest security standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}